import { useCallback, useEffect, useState, type JSX } from 'react';
import { Icon } from '../../components/Icon';
import { useStudio } from '../../context';
import { safely } from '../../safe';

export interface DeviceOption {
  deviceId: string;
  label: string;
  /** Shares hardware with the camera this picker is paired to. */
  paired?: boolean;
}

type DeviceLike = Pick<MediaDeviceInfo, 'deviceId' | 'kind' | 'label'> & Partial<Pick<MediaDeviceInfo, 'groupId'>>;

/**
 * Devices of one kind with readable labels; unlabeled ones (no permission yet) get a numbered placeholder.
 * With `pairedGroupId`, devices of that hardware group come first and are marked (a camera's own microphone).
 */
export function deviceOptions(devices: readonly DeviceLike[], kind: MediaDeviceKind, pairedGroupId?: string): DeviceOption[] {
  const names: Record<MediaDeviceKind, string> = { audioinput: 'Microphone', audiooutput: 'Speaker', videoinput: 'Camera' };
  const out: DeviceOption[] = [];
  for (const device of devices) {
    if (device.kind !== kind || device.deviceId === '' || device.deviceId === 'default') continue;
    if (out.some((o) => o.deviceId === device.deviceId)) continue;
    const paired = Boolean(pairedGroupId) && device.groupId === pairedGroupId;
    const base = device.label || `${names[kind]} ${out.length + 1}`;
    out.push({ deviceId: device.deviceId, label: paired ? `${base} (this camera)` : base, ...(paired ? { paired: true } : {}) });
  }
  return pairedGroupId ? [...out.filter((o) => o.paired), ...out.filter((o) => !o.paired)] : out;
}

/**
 * Hardware group of the camera chosen by `videoDeviceId` ('' = the first camera, which is what the browser
 * opens by default). Empty before camera permission, when groups are not exposed.
 */
export function pairedGroupOf(devices: readonly DeviceLike[], videoDeviceId: string): string | undefined {
  const cameras = devices.filter((d) => d.kind === 'videoinput' && d.deviceId !== '');
  const camera = videoDeviceId ? cameras.find((d) => d.deviceId === videoDeviceId) : cameras[0];
  return camera?.groupId || undefined;
}

/** Labels stay empty until the page has been granted access to that class of device. */
export function needsPermission(devices: readonly Pick<MediaDeviceInfo, 'kind' | 'label'>[], kind: MediaDeviceKind): boolean {
  const ofKind = devices.filter((d) => d.kind === kind);
  return ofKind.length > 0 && ofKind.every((d) => d.label === '');
}

export interface DevicePickerProps {
  id?: string;
  kind: MediaDeviceKind;
  /** '' selects the system default. */
  value: string;
  label: string;
  /** For a microphone picked next to a camera: that camera's device id, so its built-in microphone is offered first. */
  pairedVideoDeviceId?: string;
  onChange(deviceId: string): void;
}

export function DevicePicker({ id, kind, value, label, pairedVideoDeviceId, onChange }: DevicePickerProps): JSX.Element {
  const studio = useStudio();
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [supported, setSupported] = useState(true);

  const refresh = useCallback(async (): Promise<void> => {
    const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    if (!media?.enumerateDevices) {
      setSupported(false);
      return;
    }
    try {
      setDevices(await media.enumerateDevices());
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    if (!media?.addEventListener) return;
    const onChangeDevices = (): void => void refresh();
    media.addEventListener('devicechange', onChangeDevices);
    return () => media.removeEventListener('devicechange', onChangeDevices);
  }, [refresh]);

  const grant = (): void => {
    void safely(studio, async () => {
      const stream = await navigator.mediaDevices.getUserMedia(kind === 'videoinput' ? { video: true } : { audio: true });
      for (const track of stream.getTracks()) track.stop();
      await refresh();
    }, 'Access was not granted');
  };

  const options = deviceOptions(devices, kind, pairedVideoDeviceId === undefined ? undefined : pairedGroupOf(devices, pairedVideoDeviceId));
  const known = value === '' || options.some((o) => o.deviceId === value);
  return (
    <>
      <div className="ui-row">
        <select id={id} className="ui-select ui-grow" aria-label={label} value={value} disabled={!supported} onChange={(e) => onChange(e.target.value)}>
          <option value="">Default</option>
          {!known ? <option value={value}>Unavailable device ({value.slice(0, 8)})</option> : null}
          {options.map((option) => (
            <option key={option.deviceId} value={option.deviceId}>
              {option.label}
            </option>
          ))}
        </select>
        <button type="button" className="ui-icon-btn" aria-label="Refresh devices" title="Refresh devices" onClick={() => void refresh()}>
          <Icon name="refresh" />
        </button>
      </div>
      {!supported ? <span className="ui-field-help ui-warn-text">Device access needs a secure context (HTTPS or localhost).</span> : null}
      {supported && needsPermission(devices, kind) ? (
        <span className="ui-field-help">
          Device names are hidden until access is granted.{' '}
          <button type="button" className="ui-btn" onClick={grant}>
            Grant access
          </button>
        </span>
      ) : null}
    </>
  );
}
